import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, Play, Pause, SkipForward, SkipBack, BookOpen, Volume2 } from 'lucide-react';
import { apiCall, speak, stopSpeaking, shuffleQuiz } from './api';

export default function Readings({ onBack }) {
  const [list, setList] = useState([]);
  const [reading, setReading] = useState(null);
  const [idx, setIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [answers, setAnswers] = useState({});
  const [score, setScore] = useState(null);
  const [quiz, setQuiz] = useState([]);
  const [rate, setRate] = useState(0.9);
  const playingRef = useRef(false);

  const gradeQuiz = () => {
    let s = 0;
    quiz.forEach((q, qi) => { if (answers[qi] === q.answer) s += 1; });
    setScore(s);
  };

  useEffect(() => { apiCall('/readings').then(setList).catch(() => {}); }, []);
  useEffect(() => () => stopSpeaking(), []);

  const open = async (id) => {
    stopSpeaking(); setPlaying(false); playingRef.current = false;
    const r = await apiCall(`/readings/${id}`).catch(() => null);
    if (r) { setReading(r); setQuiz(shuffleQuiz(r.questions)); setIdx(0); setShowQuiz(false); setAnswers({}); setScore(null); }
  };

  const backToList = () => { stopSpeaking(); setPlaying(false); playingRef.current = false; setReading(null); };

  const playFrom = async (start) => {
    if (!reading) return;
    playingRef.current = true; setPlaying(true);
    for (let i = start; i < reading.paragraphs.length; i++) {
      if (!playingRef.current) return;
      setIdx(i);
      await speak(reading.paragraphs[i], 'en-US', rate);
      if (!playingRef.current) return;
    }
    playingRef.current = false; setPlaying(false);
  };

  const togglePlay = () => {
    if (playing) { stopSpeaking(); playingRef.current = false; setPlaying(false); }
    else { playFrom(idx); }
  };
  const next = () => { stopSpeaking(); playingRef.current = false; setPlaying(false); setIdx(i => Math.min((reading?.paragraphs.length || 1) - 1, i + 1)); };
  const prev = () => { stopSpeaking(); playingRef.current = false; setPlaying(false); setIdx(i => Math.max(0, i - 1)); };

  // ---------- LIST VIEW ----------
  if (!reading) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-5xl mx-auto px-4 py-6" data-testid="readings-page">
        <button onClick={onBack} className="flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4" data-testid="readings-back">
          <ChevronLeft size={16} /> Retour
        </button>
        <div className="flex items-center gap-3 mb-1">
          <BookOpen className="text-emerald-400" size={26} />
          <h1 className="text-2xl sm:text-3xl font-bold">Bibliothèque · Lectures audio</h1>
        </div>
        <p className="text-white/60 text-sm mb-6">Histoires, fables, biographies, humour et géographie — des textes en anglais libres de droits, à lire et à écouter à ton rythme, avec des questions de compréhension.</p>
        <div className="grid sm:grid-cols-2 gap-4">
          {list.map(r => (
            <button key={r.id} onClick={() => open(r.id)} data-testid={`reading-card-${r.id}`}
              className="text-left bg-white/5 hover:bg-white/10 border border-white/10 hover:border-emerald-500/40 rounded-2xl p-4 transition group">
              <div className="flex items-center justify-between">
                <span className="text-3xl">{r.icon}</span>
                <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-300 bg-emerald-500/10 px-2 py-0.5 rounded-full">{r.level} · {r.minutes} min</span>
              </div>
              <div className="font-bold text-lg mt-2 group-hover:text-emerald-300 transition">{r.title}</div>
              <div className="text-xs text-white/40 mb-2">par {r.author}</div>
              <p className="text-sm text-white/60">{r.intro_fr}</p>
            </button>
          ))}
          {list.length === 0 && <div className="text-white/40 text-sm">Chargement des lectures…</div>}
        </div>
      </motion.div>
    );
  }

  // ---------- READER VIEW ----------
  const total = reading.paragraphs.length;
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-3xl mx-auto px-4 py-6" data-testid="reading-reader">
      <button onClick={backToList} className="flex items-center gap-1 text-white/60 hover:text-white text-sm mb-4" data-testid="reading-back-list">
        <ChevronLeft size={16} /> Bibliothèque
      </button>

      <div className="flex items-center gap-3 mb-1">
        <span className="text-2xl">{reading.icon}</span>
        <div>
          <div className="font-bold text-xl">{reading.title}</div>
          <div className="text-xs text-white/40">par {reading.author} · {reading.level} · {reading.minutes} min</div>
        </div>
      </div>

      <div className="flex gap-1 my-4">
        {reading.paragraphs.map((_, i) => (
          <div key={i} className={`h-1 flex-1 rounded-full ${i <= idx ? 'bg-emerald-400' : 'bg-white/15'}`} />
        ))}
      </div>

      <div className="bg-slate-900/60 border border-white/10 rounded-2xl p-6 min-h-[180px] flex items-center" data-testid="reading-paragraph">
        <p className="text-xl sm:text-2xl leading-relaxed">{reading.paragraphs[idx]}</p>
      </div>

      <div className="flex items-center justify-center gap-4 mt-6">
        <button onClick={prev} disabled={idx === 0} className="p-3 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30" data-testid="reading-prev"><SkipBack size={20} /></button>
        <button onClick={togglePlay} className="p-5 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-950" data-testid="reading-play">
          {playing ? <Pause size={26} /> : <Play size={26} />}
        </button>
        <button onClick={next} disabled={idx >= total - 1} className="p-3 rounded-full bg-white/5 hover:bg-white/10 disabled:opacity-30" data-testid="reading-next"><SkipForward size={20} /></button>
      </div>

      <div className="flex items-center justify-center gap-3 mt-5 text-sm text-white/60">
        <Volume2 size={16} />
        <span>Vitesse</span>
        {[0.7, 0.9, 1.0].map(r => (
          <button key={r} onClick={() => setRate(r)} data-testid={`reading-rate-${r}`}
            className={`px-2 py-0.5 rounded-full ${rate === r ? 'bg-emerald-500 text-slate-950 font-bold' : 'bg-white/10 hover:bg-white/20'}`}>
            {r === 0.7 ? 'Lente' : r === 0.9 ? 'Normale' : 'Rapide'}
          </button>
        ))}
        <span className="ml-2">{idx + 1}/{total}</span>
      </div>

      {(reading.questions?.length > 0) && (
        <div className="mt-8" data-testid="reading-quiz">
          {!showQuiz ? (
            <button onClick={() => setShowQuiz(true)} className="w-full py-3 rounded-xl bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-200 font-bold transition" data-testid="reading-quiz-start">
              📝 Questions de compréhension ({reading.questions.length})
            </button>
          ) : (
            <div className="space-y-5">
              <h3 className="font-bold text-lg">Questions de compréhension</h3>
              <p className="text-xs text-white/50">Choisis la bonne réponse (tirée du texte).</p>
              {quiz.map((q, qi) => (
                <div key={qi} className="bg-slate-900/60 border border-white/10 rounded-xl p-4" data-testid={`reading-q-${qi}`}>
                  <div className="font-semibold mb-2">{qi + 1}. {q.q}</div>
                  <div className="space-y-2">
                    {q.options.map((opt, oi) => {
                      const chosen = answers[qi] === oi;
                      const isCorrect = q.answer === oi;
                      let cls = 'bg-white/5 hover:bg-white/10 border-white/10';
                      if (score !== null) {
                        if (isCorrect) cls = 'bg-emerald-500/20 border-emerald-400/50';
                        else if (chosen) cls = 'bg-red-500/20 border-red-400/50';
                      } else if (chosen) cls = 'bg-emerald-500/20 border-emerald-400/50';
                      return (
                        <button key={oi} disabled={score !== null}
                          onClick={() => setAnswers(a => ({ ...a, [qi]: oi }))}
                          data-testid={`reading-q-${qi}-opt-${oi}`}
                          className={`w-full text-left px-3 py-2 rounded-lg border transition text-sm ${cls}`}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {score === null ? (
                <button onClick={gradeQuiz} disabled={quiz.some((_, qi) => answers[qi] === undefined)}
                  className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold disabled:opacity-40" data-testid="reading-quiz-submit">
                  Valider mes réponses
                </button>
              ) : (
                <div className="text-center bg-emerald-500/10 border border-emerald-400/30 rounded-xl p-4" data-testid="reading-quiz-score">
                  <div className="text-3xl font-black text-emerald-300">{score}/{quiz.length}</div>
                  <div className="text-white/70 text-sm mt-1">{score === quiz.length ? 'Parfait ! 🎉' : 'Regarde les bonnes réponses en vert 👍'}</div>
                  <button onClick={() => { setScore(null); setAnswers({}); }} className="mt-3 text-xs text-emerald-300 hover:underline" data-testid="reading-quiz-retry">Recommencer</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
